import type { FacilityProtectionResult, FormState } from '@types';

const text = (value: unknown) => String(value ?? '').trim();

export function validateFacilityProtectionInput(formData: FormState): string[] {
    const missing: string[] = [];
    const assetTiers = Array.isArray(formData.fapp_asset_tiers) ? formData.fapp_asset_tiers : formData.asset_tiers;
    const frameworks = Array.isArray(formData.fapp_regulatory_frameworks) ? formData.fapp_regulatory_frameworks : formData.regulatory_frameworks;
    const address = formData.fapp_address_address || formData.address || formData.location_address || formData.location?.address;
    const country = formData.fapp_address_country || formData.country || formData.location_country;

    if (!text(formData.fapp_facility_name || formData.facility_name || formData.organization_name || formData.name))
        missing.push('Organisation / Facility Name');
    if (!text(address) || !text(country))
        missing.push('Full Address and Country');
    if (!text(formData.fapp_industry || formData.industry || formData.facility_sector))
        missing.push('Industry / Sector');
    if (!text(formData.fapp_facility_type || formData.facility_type))
        missing.push('Facility Type');
    if (!Array.isArray(assetTiers) || assetTiers.length === 0)
        missing.push('Asset Tiers');
    if (!(Number(formData.fapp_headcount || formData.headcount) > 0) || !text(formData.fapp_access_levels || formData.access_levels))
        missing.push('Headcount and Access Levels');
    if (!text(formData.fapp_existing_security || formData.existing_security_measures))
        missing.push('Existing Security Measures');
    if (!text(formData.fapp_threat_concerns || formData.known_threat_concerns))
        missing.push('Known Threat Concerns');
    if (!Array.isArray(frameworks) || frameworks.length === 0)
        missing.push('Regulatory Frameworks');
    if (!text(formData.fapp_objectives_scope || formData.fapp_purpose || formData.objectives_scope))
        missing.push('Plan Objectives and Scope');

    return missing;
}

export function calculateFacilityRiskScore(likelihood: unknown, impact: unknown): number {
    const normalizedLikelihood = Math.min(5, Math.max(1, Number(likelihood) || 1));
    const normalizedImpact = Math.min(5, Math.max(1, Number(impact) || 1));
    return normalizedLikelihood * normalizedImpact;
}

export function buildFacilityThreatAlerts(report: FacilityProtectionResult, iso3: string | null) {
    const threats = Array.isArray(report.threatAssessment) ? report.threatAssessment : [];
    const matrix = Array.isArray(report.threatAssetMatrix) ? report.threatAssetMatrix : [];
    const key = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const score = (value: unknown) => {
        if (value === null || value === undefined || value === '')
            return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 1 && parsed <= 25 ? parsed : null;
    };

    return threats.map((threat, index) => {
        const threatKey = key(threat.threat || '');
        let matrixScore: number | null = null;
        for (const row of matrix) {
            if (key(row.threat || '') !== threatKey)
                continue;
            const rowScore = score(row.riskScore);
            if (rowScore !== null)
                matrixScore = Math.max(matrixScore ?? 0, rowScore);
        }
        const riskScore = matrixScore ?? score(threat.riskScore);
        const confidence = threat.confidenceScore;
        const justification = String(threat.confidenceJustification || '').trim();
        const confidenceScore = justification
            && confidence !== null && confidence !== undefined && String(confidence).trim() !== ''
            && Number.isFinite(Number(confidence)) && Number(confidence) >= 0 && Number(confidence) <= 100
            ? Number(confidence) : undefined;
        return {
            id: `facility-threat-${index}`,
            iso3,
            lat: report.location?.lat,
            lng: report.location?.lng,
            title: threat.threat,
            summary: threat.mitigation,
            type: 'threat',
            ...(riskScore === null ? {} : { riskScore: riskScore * 4 }),
            ...(confidenceScore === undefined ? {} : { confidenceScore }),
            confidence: { ...(confidenceScore === undefined ? {} : threat.confidenceAssessment),
                justification: justification || 'No dated evidence supports a confidence score yet. Refresh confidence or add dated evidence during threat review.' },
            methodology: riskScore === null
                ? 'No scored threat-asset matrix entry exists for this threat.'
                : `Plan risk: ${riskScore}/25 from likelihood and impact, shown as ${riskScore * 4}% for map comparison.`,
            verification: { statement: report.consultantReview?.status === 'Approved'
                ? 'Consultant-approved facility plan. Threat confidence requires dated supporting evidence.'
                : 'Draft facility threat assessment. Consultant validation required.' }
        };
    });
}

export function buildFacilityFsatData(report: FacilityProtectionResult): FormState {
    return {
        ...(report.fsatData || {}),
        facility_name: report.facilityName,
        location_address: report.location?.address,
        risk_level: report.riskLevel,
        validated_asset_register: report.assetRegister || [],
        validated_threat_asset_matrix: report.threatAssetMatrix || [],
        validated_countermeasures: report.countermeasurePlan || [],
        compliance_gaps: report.complianceGaps || [],
        source_plan_version: report.workflowVersion || 'SRM-02-v2'
    };
}
